// client/src/pages/ResetPassword.jsx
import { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { API_ENDPOINTS } from '../config/api';
import { useTranslation } from 'react-i18next';

export default function ResetPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');

  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!password) {
      setError(t('resetPasswordPage.validation.passwordRequired'));
      return;
    }
    if (password !== confirmPassword) {
      setError(t('resetPasswordPage.validation.passwordMismatch'));
      return;
    }
    try {
      setLoading(true);
      setError("");

      const res = await fetch(API_ENDPOINTS.RESET_PASSWORD, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password })
      });

      const data = await res.json();

      if (res.ok) {
        setSuccess(true);
      } else {
        setError(data.detail || t('resetPasswordPage.validation.resetFailed'));
      }
    } catch {
      setError(t('resetPasswordPage.validation.genericError'));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="bg-white border border-gray-200 shadow-xl rounded-lg p-8 w-96 text-center">
          <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />

          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {t('resetPasswordPage.successTitle')}
          </h2>

          <p className="text-gray-600 mb-6">
            {t('resetPasswordPage.successMessage')}
          </p>

          <button
            onClick={() => navigate("/login")}
            className="w-full bg-primary text-white py-2 rounded-md hover:bg-blue-800 transition-colors font-medium"
          >
            {t('resetPasswordPage.goToLogin')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50">
      <form
        onSubmit={handleSubmit}
        className="bg-white border border-gray-200 shadow-xl rounded-lg p-8 w-96"
      >
        <h2 className="text-2xl font-bold mb-4 text-center text-gray-900">
          {t('resetPasswordPage.title')}
        </h2>

        <input
          type="password"
          placeholder={t('resetPasswordPage.newPassword')}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full p-3 border border-gray-300 rounded-md mb-4"
        />

        <input
          type="password"
          placeholder={t('resetPasswordPage.confirmPassword')}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          className="w-full p-3 border border-gray-300 rounded-md mb-4"
        />

        {error && <p className="text-red-600 text-sm mb-3">{error}</p>}

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-primary text-white py-2 rounded-md hover:bg-blue-800 disabled:opacity-50"
        >
          {loading ? t('resetPasswordPage.resetting') : t('resetPasswordPage.resetPassword')}
        </button>
      </form>
    </div>
  );
}
